import React from 'react';

import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';

import CrudPageLayout from '../../layouts/CrudPageLayout';
import CrudForm from '../../components/Form/CrudForm';

import UserService from '../../services/UserService';
import { useUiStore } from '../../store';

const fields = [
  {
    name: 'password',
    label: 'Password',
    type: 'password',
    fullWidth: true,
    placeholder: 'Enter your password',
    validation: Yup.string()
      .required('This field is required')
      .min(8, 'Password must be at least 8 characters')
      .test('password-strength', function (value) {
        if (!value) {
          return false;
        }

        const criteria = {
          lowercase: /[a-z]/.test(value),
          uppercase: /[A-Z]/.test(value),
          number: /\d/.test(value),
          special: /[!@#$%^&*]/.test(value),
        };

        const passedCount = Object.values(criteria).filter(Boolean).length;

        if (passedCount >= 3) {
          return true;
        }

        const missing = [];

        if (!criteria.lowercase) {
          missing.push('lowercase letter');
        }
        if (!criteria.uppercase) {
          missing.push('uppercase letter');
        }
        if (!criteria.number) {
          missing.push('number');
        }
        if (!criteria.special) {
          missing.push('special character (e.g. !@#$%^&*)');
        }

        return this.createError({
          message: `Password must contain at least ${missing.length - 1} of the following: ${missing.join(', ')}`,
          path: 'password',
        });
      }),
  },
  {
    name: 'confirmPassword',
    label: 'Confirm Password',
    type: 'password',
    fullWidth: true,
    placeholder: 'Confirm your password',
    validation: Yup.string()
      .required('This field is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  },
];

export default function AccountPage() {
  const notify = useUiStore((state) => state.notify);
  const notifApiError = useUiStore((state) => state.notifApiError);

  const { mutate: changePassword, isPending } = useMutation({
    mutationFn: async (formValues) => {
      await UserService.changePassword(formValues);
    },
    onSuccess: () => {
      notify({
        type: 'success',
        message: 'Password changed successfully',
      });
    },
    onError: (error) => {
      console.log(error);
      notifApiError(error);
    },
  });

  return (
    <CrudPageLayout title="Change Password">
      <CrudForm
        fields={fields}
        submitButtonLabel="Submit"
        createHandler={changePassword}
        isPending={isPending}
      />
    </CrudPageLayout>
  );
}
